import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button } from 'reactstrap';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import videojs from 'video.js';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

import 'video.js/dist/video-js.css';

import { setUpgradeModal } from '../actions/App';

import { sendTrackingEvent, slugify } from '../helpers';
import { sendAdobePostMessage } from '../helpers/Adobe';
import { isMiniAdobeIntegration } from '../helpers/App';
import { getUpgradePlanText } from '../helpers/UpgradePlan';

import LearnMoreLink from '../containers/EmptyContainers/components/LearnMoreLink';
import EmptyDuplicates from '../containers/EmptyContainers/Duplicates';
import ErrorBoundary from './ErrorBoundary';

const UpgradeBoard = ({
  features,
  trialEndsAt,
  isInTrial,
  feature,
  buttonCallback,
}) => {
  const [buttonHovered, setButtonHovered] = useState(false);
  const intl = useIntl();

  const isAdobeIntegration = isMiniAdobeIntegration();

  const lockedFeature = features.find(f => (f.slug === feature));

  const plan = lockedFeature && lockedFeature.plans
    .reduce((lowest, featurePlan) => (featurePlan.position < lowest.position ?
      featurePlan : lowest), lockedFeature.plans[0]);

  const planTexts = getUpgradePlanText(feature, intl);

  const upgradeLink = 'https://stockpress.co/pricing/#features';

  const videoRef = useRef(null);
  const playerRef = useRef(null);

  const videoPath = feature && planTexts.video
    ? `/videos/feature-showcase-videos/${planTexts.video}.webm`
    : null;

  useEffect(() => () => {
    if (playerRef.current) {
      playerRef.current.dispose();
      playerRef.current = null;
    }
  }, []);

  const trackClick = () => {
    sendTrackingEvent('upgrade-button-clicked', { source: feature });
  };

  useEffect(() => {
    if (!videoRef.current || !videoPath) return () => {};

    if (playerRef.current) {
      playerRef.current.dispose();
      playerRef.current = null;
    }

    playerRef.current = videojs(videoRef.current, {
      controls: false,
      autoplay: true,
      muted: true,
      preload: 'auto',
      loop: true,
      sources: [
        {
          src: videoPath,
          type: 'video/webm',
        },
      ],
    });

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [videoPath]);

  if (!feature) return null;

  if (!lockedFeature) {
    return null;
  }
  if ((!lockedFeature.plans || lockedFeature.plans.length === 0)) {
    return null;
  }

  return (
    <ErrorBoundary>
      <div className={classnames(`upgrade-board upgrade-board-${plan.name.toLowerCase()}`, { 'upgrade-board-image-wrapper': !planTexts.video && planTexts.image })}>
        <div className="upgrade-board-content">
          <p className="upgrade-board-content-notice">
            <i className="fas fa-arrow-up" />
            {intl.formatMessage(
              { defaultMessage: 'Upgrade to <strong>{planName}</strong> to unlock this feature' },
              {
                planName: plan.name,
                strong: (msg) => <strong>{msg}</strong>,
              },
            )}
          </p>
          <h1>
            {planTexts.header}
          </h1>
          {planTexts.subheader && (
            <h2>
              {planTexts.subheader}
            </h2>
          )}
          {feature === 'duplicate-files' && (
            <EmptyDuplicates />
          )}
          <ul className="upgrade-fa-ul fa-ul">
            {planTexts.others.map(other => (
              <li key={slugify(other)}>
                {other}
              </li>
            ))}
            {planTexts.others.length > 0 && (
              <li>
                <a
                  href={isAdobeIntegration ? '#upgrade' : upgradeLink}
                  target="_blank"
                  rel="noreferrer"
                  className="upgrade-plan-button-a"
                  onClick={(e) => {
                    e.preventDefault();
                    if (isMiniAdobeIntegration()) {
                      sendAdobePostMessage('open-link', upgradeLink);
                    }
                  }}
                >
                  {intl.formatMessage({ defaultMessage: 'and so much more!' })}
                </a>
              </li>
            )}
          </ul>
          <Button
            className={`upgrade-plan-button-a upgrade-plan-button-${plan.slug} upgrade-plan-button-${buttonHovered ? 'hovered' : 'default'}`}
            tag={Link}
            to="/admin/settings/subscription"
            onMouseEnter={() => setButtonHovered(true)}
            onMouseLeave={() => setButtonHovered(false)}
            onClick={() => {
              trackClick();
              if (isMiniAdobeIntegration()) {
                sendAdobePostMessage('open-link', 'https://stockpress.co/pricing/#features');
              }
              buttonCallback();
            }}
          >
            <i className="fas fa-fw mr-2 fa-arrow-up" />
            {isInTrial ? (
              intl.formatMessage({ defaultMessage: 'Upgrade to {planName}' }, { planName: plan.name })
            ) : (
              <>
                {trialEndsAt ? (
                  intl.formatMessage(
                    { defaultMessage: 'Upgrade to {planName}' },
                    { planName: plan.name },
                  )
                ) : (
                  intl.formatMessage({ defaultMessage: 'Try it for free' })
                )}
              </>
            )}
          </Button>
          {planTexts.learnMore && (
            <LearnMoreLink
              url={planTexts.learnMore}
              className="learn-more-link"
              {...(planTexts.learnMoreText ? { text: planTexts.learnMoreText } : {})}
            />
          )}
        </div>
        {(planTexts.video) && (
          <div className="upgrade-board-media-container">
            <div data-vjs-player>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                ref={videoRef}
                className="video-js vjs-default-skin"
                playsInline
              />
            </div>
          </div>
        )}
        {(!planTexts.video && planTexts.image) && (
          <div className="upgrade-board-media-container">
            <div className="position-relative mr-1">
              <img
                src="/images/empty-pages/shape1-bg.svg"
                alt=""
                className="position-absolute w-100 h-100 image-shape"
              />
              <img
                src={planTexts.image}
                alt=""
                className={classnames('img-fluid position-relative image-main')}
              />
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  trialEndsAt: state.Auth.profile?.client?.trial_ends_at,
  isInTrial: state.Auth.profile?.client?.is_in_trial,
  features: state.App.features,
});

const mapDispatchToProps = dispatch => ({
  handleUpgradeModal: (modal) => {
    dispatch(setUpgradeModal(modal));
  },
});

UpgradeBoard.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  handleUpgradeModal: PropTypes.func.isRequired,
  feature: PropTypes.string.isRequired,
  features: PropTypes.array,
  trialEndsAt: PropTypes.string,
  isInTrial: PropTypes.bool,
  buttonCallback: PropTypes.func,
};

UpgradeBoard.defaultProps = {
  features: [],
  trialEndsAt: null,
  isInTrial: false,
  buttonCallback: () => {},
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(UpgradeBoard));
